// Authentication modal component
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import LoginForm from './LoginForm.js';
import RegisterForm from './RegisterForm.js';
import { useAuth } from '../contexts/AuthContext.js';
import firebaseAuth from '../services/FirebaseAuth.js';
import AuthDiagnostic from './AuthDiagnostic.js';
import './AuthModal.css';

export default function AuthModal({ isOpen, onClose, initialMode = 'login' }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialMode);
  const [showDebug, setShowDebug] = useState(false);
  const { login, register, signInWithGoogle, loading, authError } = useAuth();

  if (!isOpen) return null;

  const handleLogin = async (email, password) => {
    const result = await login(email, password);
    if (result.success) {
      onClose();
    }
  };

  const handleRegister = async (email, password, displayName) => {
    const result = await register(email, password, displayName);
    if (result.success) {
      onClose();
    }
  };

  const handleGoogleSignIn = async () => {
    const result = await signInWithGoogle();
    if (result.success) {
      if (result.pending) {
        // For mobile redirect, don't close modal as page will reload
        // The redirect will handle the authentication flow
        return;
      }
      // For popup success (desktop), close the modal
      onClose();
    } else {
      // Handle Safari storage partitioning issues
      if (result.errorType === 'safari-storage-issue' && result.suggestPopup) {
        const tryPopup = window.confirm(
          'Safari redirect authentication failed due to storage restrictions. ' +
          'Would you like to try popup authentication instead? ' +
          '(You may need to allow popups for this site)'
        );

        if (tryPopup) {
          // Force popup authentication for Safari
          console.log('🍎 User chose to try popup authentication on Safari');
          // The error handling in AuthContext will display the error message
        }
      }
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDebugAuth = async () => {
    console.log('🔧 Running authentication debug test...');
    const debugInfo = firebaseAuth.getDeviceDebugInfo();
    const testResults = await firebaseAuth.testAuthConfiguration();

    console.log('📱 Device Debug Info:', debugInfo);
    console.log('🔧 Auth Test Results:', testResults);

    let debugMessage = `Debug Info:\n\nDevice: ${debugInfo.isSafariMobile ? 'Safari Mobile' : 'Other'}\nWill use: ${debugInfo.willUseRedirect ? 'Redirect' : 'Popup'}\nUser Agent: ${debugInfo.userAgent}`;

    if (debugInfo.isSafariMobile) {
      debugMessage += '\n\n🍎 Safari Mobile Detected:\n- Will try popup first, then redirect\n- Storage partitioning may cause issues\n- Check console for detailed flow';
    }

    debugMessage += '\n\nCheck console for detailed results.';

    alert(debugMessage);
  };

  return (
    <div className="auth-modal-backdrop" onClick={handleBackdropClick}>
      <div className="auth-modal">
        <button 
          className="auth-modal-close"
          onClick={onClose}
          disabled={loading}
        >
          ×
        </button>
        
        <div className="auth-modal-content">
          {mode === 'login' ? (
            <LoginForm
              onLogin={handleLogin}
              onGoogleSignIn={handleGoogleSignIn}
              onSwitchToRegister={() => setMode('register')}
              loading={loading}
              error={authError}
            />
          ) : (
            <RegisterForm
              onRegister={handleRegister}
              onGoogleSignIn={handleGoogleSignIn}
              onSwitchToLogin={() => setMode('login')}
              loading={loading}
              error={authError}
            />
          )}
        </div>
        
        <div className="auth-modal-footer">
          <button
            className="auth-button secondary"
            onClick={onClose}
            disabled={loading}
          >
            {t('auth.continueAsGuest')}
          </button>

          {/* Debug button for development - only show in development mode */}
          {process.env.NODE_ENV === 'development' && (
            <button
              className="auth-button secondary"
              onClick={() => setShowDebug(!showDebug)}
              disabled={loading}
              style={{ marginLeft: '10px', fontSize: '12px' }}
            >
              {showDebug ? 'Hide Debug' : 'Debug Auth'}
            </button>
          )}
        </div>

        {/* Debug section */}
        {showDebug && process.env.NODE_ENV === 'development' && (
          <div style={{
            padding: '10px',
            backgroundColor: '#f5f5f5',
            borderTop: '1px solid #ddd',
            fontSize: '12px'
          }}>
            <button
              onClick={handleDebugAuth}
              style={{
                padding: '5px 10px',
                fontSize: '11px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer',
                marginBottom: '10px'
              }}
            >
              Run Auth Debug Test
            </button>
            <p style={{ margin: '5px 0', fontSize: '11px', color: '#666' }}>
              This will test authentication configuration and log device info to console.
            </p>

            <hr style={{ margin: '10px 0', borderColor: '#ddd' }} />

            <AuthDiagnostic />
          </div>
        )}
      </div>
    </div>
  );
}
